import React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { orderBy } from 'lodash'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import Sidepanel from 'modules/core/components/Sidepanel'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import { StepProgressionCondtion } from 'modules/processes/components/ProcessSidePanel'
import { Step } from 'modules/processes/domain'
import {
  type BulkUpdateSteps,
  useBulkUpdateSteps,
} from 'modules/processes/hooks/useBulkUpdateSteps'
import { useCreateStep } from 'modules/processes/hooks/useCreateStep'
import { DueDatesFields } from 'modules/processes/hooks/useProcesses'
import {
  type StepFormValues,
  buildStepsWithPositionsForCreateAction,
  buildStepsWithPositionsForUpdateAction,
} from 'modules/processes/lib/steps'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'
import { useValidateStringLength } from 'modules/tasks/hooks/useValidateStringLength'

import OffsetOptions from './OffsetOptions'
import StepForm from './StepForm'

interface Props {
  steps: Step[]
  stepId?: string | null
  onClose: () => void
  processId: string
}

const AFTER = 'after'
const BEFORE = 'before'

const initialPositionData = (steps: Step[], stepId?: string | null) => {
  if (steps.length === 1 || !stepId)
    return { step_to_swap_id: steps[steps.length - 1]?.id, before_or_after: AFTER }

  const currentStepIndex = steps.findIndex((step) => step.id === stepId)
  if (currentStepIndex + 1 === steps.length)
    return { step_to_swap_id: steps[currentStepIndex - 1]?.id, before_or_after: AFTER }

  return { step_to_swap_id: steps[currentStepIndex + 1]?.id, before_or_after: BEFORE }
}

export const positionOptions = (): SelectOption[] => [
  {
    label: i18n.t('processes.process_settings.steps.modal.before'),
    value: BEFORE,
  },
  {
    label: i18n.t('processes.process_settings.steps.modal.after'),
    value: AFTER,
  },
]

const StepUpsertModal = ({ steps, stepId, processId, onClose }: Props) => {
  const { process } = useProcessContext()
  const { createStep } = useCreateStep()
  const { bulkUpdateSteps } = useBulkUpdateSteps()
  const { validate, isValid: isStepNameValid } = useValidateStringLength()

  const [stepsToBeUpdated, setStepsToBeUpdated] = React.useState<Step[] | null>(null)
  const [newStepPosition, setNewStepPosition] = React.useState<number | null>(null)
  const currentStep = steps.find((step) => step.id == stepId)
  const isNew = !stepId

  const orderedSteps = orderBy(steps, (step) => step.position)
  const stepProgressionConditionResourceKey = currentStep?.progressionCondition
    ? currentStep?.progressionConditionResourceKey
    : DueDatesFields.ProcessStartDate
  const form = useLazyRef(
    () =>
      new StepForm({
        id: currentStep?.id,
        name: currentStep?.name,
        position: currentStep?.position,
        offset_direction: currentStep?.offsetDirection || 'after',
        offset_amount: currentStep?.offsetAmount || 0,
        progression_condition_resource_key: stepProgressionConditionResourceKey,
        progression_condition: currentStep?.progressionCondition || StepProgressionCondtion.ByDate,
        ...initialPositionData(orderedSteps, stepId),
      })
  ).current

  const stepsOption: SelectOption[] = steps
    .filter((step) => step.id !== currentStep?.id)
    .map((step) => ({ value: step.id, label: step.name }))

  const {
    name: stepName,
    before_or_after: beforeOrAfter,
    step_to_swap_id: stepToSwapId,
    offset_direction: offsetDirectionValue,
    offset_amount: offsetAmount,
    progression_condition_resource_key: progressionConditionResourceKey,
    progression_condition: progressionCondition,
  } = form.values

  const processProgressionByDate = progressionCondition === StepProgressionCondtion.ByDate

  useEffect(() => {
    if (isNew) {
      const data = buildStepsWithPositionsForCreateAction(
        orderedSteps,
        form.values as StepFormValues // eslint-disable-line no-type-assertion/no-type-assertion
      )
      setStepsToBeUpdated(data.stepsToBeUpdated)
      setNewStepPosition(data.newStepPosition)
    } else {
      if (currentStep) {
        validate(currentStep.name)
      }
      const stepsWithCurrentStepUpdate = orderedSteps.map((step) => {
        if (step.id === String(currentStep?.id)) {
          return {
            ...step,
            name: stepName,
            progressionCondition,
            progressionConditionResourceKey,
            offsetDirection: offsetDirectionValue,
            offsetAmount: Number(offsetAmount),
          }
        }
        return step
      })
      setStepsToBeUpdated(
        buildStepsWithPositionsForUpdateAction(
          stepsWithCurrentStepUpdate,
          stepsWithCurrentStepUpdate.find((step) => step.id === String(currentStep?.id))!, // eslint-disable-line no-type-assertion/no-type-assertion
          form.values as StepFormValues // eslint-disable-line no-type-assertion/no-type-assertion
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stepName,
    beforeOrAfter,
    stepToSwapId,
    offsetDirectionValue,
    offsetAmount,
    progressionConditionResourceKey,
    progressionCondition,
  ])

  useEffect(() => {
    if (stepName) validate(stepName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepName])

  const { performAction, running } = useAsyncAction(async () => {
    if (isNew)
      await createStep({
        name: stepName,
        processId,
        progressionCondition,
        progressionConditionResourceKey,
        position: Number(newStepPosition),
        ...(processProgressionByDate
          ? {
              offsetDirection: offsetDirectionValue,
              offsetAmount: +offsetAmount,
            }
          : {}),
      })

    if (stepsToBeUpdated?.length) {
      await bulkUpdateSteps(
        stepsToBeUpdated.map(
          (step) =>
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            ({
              id: String(step.id),
              name: step.name,
              position: step.position,
              progressionCondition: step.progressionCondition,
              progressionConditionResourceKey: step.progressionConditionResourceKey,
              ...(processProgressionByDate
                ? {
                    offsetDirection: step.offsetDirection,
                    offsetAmount: Number(step.offsetAmount),
                  }
                : {}),
            }) as BulkUpdateSteps
        ),
        processId
      )
    }

    onClose()
  })

  const disableSubmitButton =
    !form.get('name').hasValue ||
    !form?.isDirty ||
    running ||
    !isStepNameValid ||
    !progressionCondition

  const showStepSelection =
    progressionCondition && ((isNew && steps.length >= 1) || steps.length > 1)

  return (
    <Sidepanel
      open
      overlay
      boxPadding
      width="wide"
      title={
        isNew
          ? i18n.t('processes.process_settings.steps.modal.add_title')
          : i18n.t('processes.process_settings.steps.modal.edit_title')
      }
      onClose={onClose}
      headerBorder
      closeLabel={i18n.t('common.close')}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>

          <Button onClick={performAction} disabled={disableSubmitButton}>
            {isNew
              ? i18n.t('processes.process_settings.steps.modal.add_title')
              : i18n.t('processes.process_settings.steps.modal.edit_title')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <TextInput
          mandatory
          validation={validate}
          field={form.get('name')}
          label={i18n.t('processes.process_settings.steps.modal.step_name')}
        />
        {showStepSelection && (
          <Box>
            <Box paddingBottom="s4">
              <Text.Normal weight="medium" size="200" color="grey1000">
                {i18n.t('processes.process_settings.steps.modal.position')}
              </Text.Normal>
            </Box>
            <Box gap="s12" flexDirection="row">
              <Select field={form.get('before_or_after')} options={positionOptions()} />
              <Select field={form.get('step_to_swap_id')} options={stepsOption} />
            </Box>
          </Box>
        )}
        <Box>
          <OffsetOptions form={form} process={process} />
        </Box>
      </Box>
    </Sidepanel>
  )
}

export default observer(StepUpsertModal)
